import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import paymentReminderService from '../../../services/paymentReminderService';

const PaymentReconciliationPanel = ({ companyId }) => {
  const [reconciliationData, setReconciliationData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [realtimeChannel, setRealtimeChannel] = useState(null);

  useEffect(() => {
    loadReconciliationData();
    setupRealtimeSubscription();

    return () => {
      if (realtimeChannel) {
        paymentReminderService?.unsubscribeFromPaymentUpdates(realtimeChannel);
      }
    };
  }, [companyId]);

  const loadReconciliationData = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await paymentReminderService?.getPaymentReconciliationStatus(companyId);
      setReconciliationData(data);
    } catch (err) {
      setError(err?.message || 'Failed to load reconciliation data');
    } finally {
      setLoading(false);
    }
  };

  const setupRealtimeSubscription = () => {
    const channel = paymentReminderService?.subscribeToPaymentUpdates(companyId, (update) => {
      console.log('Real-time payment update:', update);
      // Reload data when changes detected
      loadReconciliationData();
    });
    setRealtimeChannel(channel);
  };

  const handleRefresh = () => {
    loadReconciliationData();
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-center py-8">
          <Icon name="Loader" className="w-6 h-6 text-primary animate-spin" />
          <span className="ml-2 text-gray-600">Loading reconciliation data...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="text-center py-8">
          <Icon name="AlertCircle" className="w-12 h-12 text-error mx-auto mb-3" />
          <p className="text-error">{error}</p>
          <button
            onClick={handleRefresh}
            className="mt-4 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200">
      {/* Header */}
      <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
            <Icon name="Activity" className="w-5 h-5 text-primary" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Payment Reconciliation Status</h3>
            <p className="text-sm text-gray-500">Real-time tracking of payments and invoices</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-2 px-3 py-1 bg-success/10 text-success rounded-full text-sm">
            <div className="w-2 h-2 bg-success rounded-full animate-pulse" />
            <span>Live</span>
          </div>
          <button
            onClick={handleRefresh}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            title="Refresh data"
          >
            <Icon name="RefreshCw" className="w-5 h-5 text-gray-600" />
          </button>
        </div>
      </div>

      {/* Reconciliation Metrics */}
      <div className="p-6 space-y-6">
        {/* Reconciliation Status Overview */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-green-800">Reconciled</span>
              <Icon name="CheckCircle" className="w-5 h-5 text-green-600" />
            </div>
            <p className="text-2xl font-bold text-green-900">{reconciliationData?.reconciliation?.reconciled || 0}</p>
            <p className="text-xs text-green-600 mt-1">
              {reconciliationData?.reconciliation?.reconciliationRate || 0}% of total
            </p>
          </div>

          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-amber-800">Pending</span>
              <Icon name="Clock" className="w-5 h-5 text-amber-600" />
            </div>
            <p className="text-2xl font-bold text-amber-900">{reconciliationData?.reconciliation?.pending || 0}</p>
            <p className="text-xs text-amber-600 mt-1">Awaiting reconciliation</p>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-blue-800">Processing</span>
              <Icon name="Loader" className="w-5 h-5 text-blue-600 animate-spin" />
            </div>
            <p className="text-2xl font-bold text-blue-900">{reconciliationData?.reconciliation?.processing || 0}</p>
            <p className="text-xs text-blue-600 mt-1">Being processed</p>
          </div>

          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-red-800">Failed</span>
              <Icon name="XCircle" className="w-5 h-5 text-red-600" />
            </div>
            <p className="text-2xl font-bold text-red-900">{reconciliationData?.reconciliation?.failed || 0}</p>
            <p className="text-xs text-red-600 mt-1">Requires attention</p>
          </div>
        </div>

        {/* Payment Methods Breakdown */}
        <div className="border border-gray-200 rounded-lg p-4">
          <h4 className="text-sm font-semibold text-gray-900 mb-4 flex items-center">
            <Icon name="CreditCard" className="w-4 h-4 mr-2 text-gray-600" />
            Payment Methods
          </h4>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="text-center">
              <p className="text-2xl font-bold text-gray-900">{reconciliationData?.paymentMethods?.stripe || 0}</p>
              <p className="text-xs text-gray-600 mt-1">Stripe</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-gray-900">{reconciliationData?.paymentMethods?.bsp || 0}</p>
              <p className="text-xs text-gray-600 mt-1">BSP</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-gray-900">{reconciliationData?.paymentMethods?.kinabank || 0}</p>
              <p className="text-xs text-gray-600 mt-1">Kinabank</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-gray-900">{reconciliationData?.paymentMethods?.creditbank || 0}</p>
              <p className="text-xs text-gray-600 mt-1">Credit Bank</p>
            </div>
          </div>
        </div>

        {/* Financial Summary */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="border border-gray-200 rounded-lg p-4">
            <p className="text-sm text-gray-600 mb-1">Total Revenue</p>
            <p className="text-2xl font-bold text-gray-900">K{reconciliationData?.financial?.totalRevenue || '0.00'}</p>
          </div>
          <div className="border border-gray-200 rounded-lg p-4">
            <p className="text-sm text-gray-600 mb-1">Outstanding</p>
            <p className="text-2xl font-bold text-amber-600">K{reconciliationData?.financial?.outstandingAmount || '0.00'}</p>
          </div>
          <div className="border border-gray-200 rounded-lg p-4">
            <p className="text-sm text-gray-600 mb-1">Collection Rate</p>
            <p className="text-2xl font-bold text-green-600">{reconciliationData?.financial?.collectionRate || 0}%</p>
          </div>
        </div>

        {/* Last Updated */}
        <div className="text-center text-xs text-gray-500">
          Last updated: {reconciliationData?.lastUpdated ? new Date(reconciliationData?.lastUpdated)?.toLocaleString() : 'N/A'}
        </div>
      </div>
    </div>
  );
};

export default PaymentReconciliationPanel;